import { Component } from '@angular/core';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Setup } from '../../setup/shared/service/setup';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { ToastService } from '../../../core/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faPencil, faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import { CommonModule } from '@angular/common';
import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { FormsModule } from '@angular/forms';

export interface PaymentTypeUI {
  typSeq: number;
  typId: number;
  typStr: string;
  typStsKey: number;
  prdSeq: number;
  glAcctNum: string;
}

@Component({
  selector: 'app-payment-types',
  imports: [CommonModule, Table, Popup, FormsModule, TranslatePipe],
  templateUrl: './payment-type.html',
  styleUrls: ['./payment-type.css']
})
export class PaymentTypesComponent {
  faPencil = faPencil;
  faTrash = faTrash;
  faSync = faSync;

  data: PaymentTypeUI[] = [];
  selectedRow: PaymentTypeUI | null = null;
  showPopup = false;
  popupTitle = '';
  isDeleteConfirm = false;
  popupFields: DynamicFieldConfig[] = [];

  productOptions: { label: string, value: number }[] = [];
  productKey = 13;

  constructor(
    private setupService: Setup,
    private pickListService: PickListService,
    private toast: ToastService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadPaymentTypes();
  }

 loadPaymentTypes(): void {
  this.setupService.getTypes(4).subscribe({
    next: (res: PaymentTypeUI[]) => {
      this.data = res.map(item => ({
        typSeq: item.typSeq,
        typId: item.typId,
        typStr: item.typStr || '',
        typStsKey: item.typStsKey || 0,
        prdSeq: item.prdSeq || 0,
        glAcctNum: item.glAcctNum || '',
        status: item.typStsKey === 1 ? 'Active' : 'Inactive'
      }));

      // ✅ Sort: Letters first (A-Z), then numbers — by Payment Type
      this.data.sort((a, b) => {
        const nameA = (a.typStr || '').trim();
        const nameB = (b.typStr || '').trim();

        const aStartsWithNumber = /^\d/.test(nameA);
        const bStartsWithNumber = /^\d/.test(nameB);

        // Letters pehle, numbers baad me
        if (aStartsWithNumber && !bStartsWithNumber) return 1;
        if (!aStartsWithNumber && bStartsWithNumber) return -1;

        // Dono same type → normal sort
        return nameA.localeCompare(nameB, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      });
    },
    error: err => console.error(err)
  });
}

  // ================= TABLE COLUMNS =================
  columns: TableColumn[] = [
    { key: 'typSeq', label: 'GL.PAYMENT_TYPE.ID' },
    { key: 'typStr', label: 'GL.PAYMENT_TYPE.NAME' },
    { key: 'glAcctNum', label: 'GL.GL_ACCOUNT' },
    { key: 'status', label: 'COMMON.STATUS', type: 'map', map: { Active: 'COMMON.ACTIVE', Inactive: 'COMMON.INACTIVE' } }
  ];

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-green-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: PaymentTypeUI) => this.openEditPopup(row)
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: PaymentTypeUI) => this.openDeletePopup(row)
    },
    {
      icon: this.faSync,
      class: 'text-black',
      tooltip: 'COMMON.REFRESH',
      onClick: () => this.loadPaymentTypes()
    }
  ];

  // ================= POPUP =================
  openAddPopup(): void {
    this.selectedRow = null;
    this.isDeleteConfirm = false;
    this.popupTitle = 'GL.PAYMENT_TYPE.ADD';
    this.popupFields = [
      { key: 'typStr', label: 'GL.PAYMENT_TYPE.NAME', type: 'text', validations: { required: true } },
      { key: 'glAcctNum', label: 'GL.GL_ACCOUNT', type: 'text', validations: { required: true } },

      {
        key: 'typStsKey', label: 'COMMON.STATUS', type: 'select', value: 1, options$: [
          { label: 'COMMON.ACTIVE', value: 1 },
          { label: 'COMMON.INACTIVE', value: 0 }
        ]
      }
    ];
    this.showPopup = true;
  }

  openEditPopup(row: PaymentTypeUI): void {
    this.selectedRow = row;
    this.isDeleteConfirm = false;
    this.popupTitle = 'GL.PAYMENT_TYPE.EDIT';
    this.popupFields = [
      { key: 'typStr', label: 'GL.PAYMENT_TYPE.NAME', type: 'text', value: row.typStr, validations: { required: true } },
      { key: 'glAcctNum', label: 'GL.GL_ACCOUNT', type: 'text', value: row.glAcctNum, validations: { required: true } },
      {
        key: 'typStsKey', label: 'COMMON.STATUS', type: 'select', value: row.typStsKey, options$: [
          { label: 'COMMON.ACTIVE', value: 1 },
          { label: 'COMMON.INACTIVE', value: 0 }
        ]
      }
    ];
    this.showPopup = true;
  }

  openDeletePopup(row: PaymentTypeUI): void {
    this.selectedRow = row;
    this.isDeleteConfirm = true;
    this.popupTitle = 'GL.PAYMENT_TYPE.DELETE';
    this.popupFields = [
      { label: this.translate.instant('GL.PAYMENT_TYPE.DELETE_CONFIRM', { id: row.typSeq }), key: 'confirm', type: 'message' }
    ];
    this.showPopup = true;
  }

  handleSave(formData: any): void {

    const isDelete = this.isDeleteConfirm && this.selectedRow;

    const payload = {
      typSeq: this.selectedRow ? this.selectedRow.typSeq : null,
      typId: this.selectedRow ? this.selectedRow.typId : 0,
      typStr: isDelete
        ? this.selectedRow?.typStr
        : formData.typStr,

      prdSeq: isDelete
        ? this.selectedRow?.prdSeq
        : formData.prdSeq,

      glAcctNum: isDelete
        ? this.selectedRow?.glAcctNum
        : formData.glAcctNum,

      typStsKey: isDelete
        ? this.selectedRow?.typStsKey
        : formData.typStsKey,

      typCtgryKey: 4,
      delFlg: isDelete ? 1 : 0
    };

    this.setupService.savetype(payload).subscribe({
      next: () => {

        if (isDelete) {
          this.toast.success('GL.DELETED_SUCCESSFULLY');
        } else if (this.selectedRow) {
          this.toast.success('GL.UPDATED_SUCCESSFULLY');
        } else {
          this.toast.success('GL.SAVED_SUCCESSFULLY');
        }

        this.showPopup = false;
        this.loadPaymentTypes();
      },
      error: err => {
        console.error('Operation failed', err);
        this.toast.error('GL.OPERATION_FAILED');
      }
    });
  }
}